import React, { useState, useEffect } from 'react';
import { Scissors, Download, FileText, Trash2 } from 'lucide-react';
import { Button, Card, FileUploadZone, ProgressBar, Alert, Badge, ToolActionButtons, PDFPreview } from '../shared';
import { PDFDocument } from 'pdf-lib';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { toast } from 'react-toastify';
import '../../styles/design-system.css';

const SplitPDF = () => {
    const [file, setFile] = useState(null);
    const [pdfDoc, setPdfDoc] = useState(null);
    const [totalPages, setTotalPages] = useState(0);
    const [splitMode, setSplitMode] = useState('pages'); // 'pages', 'ranges', 'every'
    const [selectedPages, setSelectedPages] = useState([]);
    const [ranges, setRanges] = useState([{ start: '', end: '' }]);
    const [everyNPages, setEveryNPages] = useState(1);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [previewBlob, setPreviewBlob] = useState(null);
    const [previewFilename, setPreviewFilename] = useState('');
    const [showPreview, setShowPreview] = useState(false);

    const handleFileSelected = async (files) => {
        if (files && files.length > 0) {
            const selectedFile = files[0];
            setFile(selectedFile);

            try {
                const arrayBuffer = await selectedFile.arrayBuffer();
                const pdf = await PDFDocument.load(arrayBuffer);
                setPdfDoc(pdf);
                setTotalPages(pdf.getPageCount());
                setSelectedPages([]);
                toast.success(`PDF loaded: ${pdf.getPageCount()} pages`);
            } catch (error) {
                toast.error(`Failed to load PDF: ${error.message}`);
            }
        }
    };

    const togglePageSelection = (pageNum) => {
        setSelectedPages(prev => {
            if (prev.includes(pageNum)) {
                return prev.filter(p => p !== pageNum);
            } else {
                return [...prev, pageNum].sort((a, b) => a - b);
            }
        });
    };

    const selectAllPages = () => {
        setSelectedPages(Array.from({ length: totalPages }, (_, i) => i + 1));
    };

    const deselectAllPages = () => {
        setSelectedPages([]);
    };

    const addRange = () => {
        setRanges([...ranges, { start: '', end: '' }]);
    };

    const updateRange = (index, field, value) => {
        const newRanges = [...ranges];
        newRanges[index][field] = value;
        setRanges(newRanges);
    };

    const removeRange = (index) => {
        setRanges(ranges.filter((_, i) => i !== index));
    };

    const splitByPages = async () => {
        if (selectedPages.length === 0) {
            toast.warning('Please select at least one page');
            return;
        }

        try {
            setIsProcessing(true);
            setProgress(0);

            const zip = new JSZip();
            // Create preview PDF with all selected pages
            const previewPdf = await PDFDocument.create();

            for (let i = 0; i < selectedPages.length; i++) {
                const pageNum = selectedPages[i];
                setProgress(((i + 1) / selectedPages.length) * 80);

                // Create a new PDF with just this page for ZIP
                const newPdf = await PDFDocument.create();
                const [copiedPage] = await newPdf.copyPages(pdfDoc, [pageNum - 1]);
                newPdf.addPage(copiedPage);

                const pdfBytes = await newPdf.save();
                zip.file(`page-${pageNum}.pdf`, pdfBytes);

                // Add page to preview PDF
                const [previewPage] = await previewPdf.copyPages(pdfDoc, [pageNum - 1]);
                previewPdf.addPage(previewPage);
            }

            setProgress(85);

            // Generate preview PDF
            const previewBytes = await previewPdf.save();
            const previewBlob = new Blob([previewBytes], { type: 'application/pdf' });
            const timestamp = new Date().toISOString().slice(0, 10);
            const previewFilename = `split-preview-${timestamp}.pdf`;
            
            setPreviewBlob(previewBlob);
            setPreviewFilename(previewFilename);

            setProgress(95);

            // Generate ZIP file
            const zipBlob = await zip.generateAsync({ type: 'blob' });
            setProgress(100);

            saveAs(zipBlob, `split-pdf-pages-${timestamp}.zip`);

            toast.success(`${selectedPages.length} pages extracted successfully! Opening preview...`);

            setTimeout(() => {
                setProgress(0);
            }, 1000);

        } catch (error) {
            console.error('Split error:', error);
            toast.error(`Failed to split PDF: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    const splitByRanges = async () => {
        // Validate ranges
        const validRanges = ranges.filter(r => r.start && r.end);
        if (validRanges.length === 0) {
            toast.warning('Please specify at least one valid range');
            return;
        }

        try {
            setIsProcessing(true);
            setProgress(0);

            const zip = new JSZip();
            // Create preview PDF with first range
            const previewPdf = await PDFDocument.create();
            let previewCreated = false;

            for (let i = 0; i < validRanges.length; i++) {
                const range = validRanges[i];
                const start = parseInt(range.start);
                const end = parseInt(range.end);

                if (start < 1 || end > totalPages || start > end) {
                    toast.error(`Invalid range: ${start}-${end}`);
                    continue;
                }

                setProgress(((i + 1) / validRanges.length) * 80);

                // Create a new PDF with pages in this range
                const newPdf = await PDFDocument.create();
                const pageIndices = Array.from({ length: end - start + 1 }, (_, idx) => start - 1 + idx);
                const copiedPages = await newPdf.copyPages(pdfDoc, pageIndices);
                copiedPages.forEach(page => newPdf.addPage(page));

                const pdfBytes = await newPdf.save();
                zip.file(`pages-${start}-to-${end}.pdf`, pdfBytes);

                // Create preview from first range
                if (!previewCreated) {
                    copiedPages.forEach(page => previewPdf.addPage(page));
                    previewCreated = true;
                }
            }

            setProgress(85);

            // Generate preview PDF
            if (previewCreated) {
                const previewBytes = await previewPdf.save();
                const previewBlob = new Blob([previewBytes], { type: 'application/pdf' });
                const timestamp = new Date().toISOString().slice(0, 10);
                const previewFilename = `split-preview-${timestamp}.pdf`;
                
                setPreviewBlob(previewBlob);
                setPreviewFilename(previewFilename);
            }

            setProgress(95);

            // Generate ZIP file
            const zipBlob = await zip.generateAsync({ type: 'blob' });
            setProgress(100);

            const timestamp = new Date().toISOString().slice(0, 10);
            saveAs(zipBlob, `split-pdf-ranges-${timestamp}.zip`);

            toast.success(`PDF split into ${validRanges.length} files! Opening preview...`);

            setTimeout(() => {
                setProgress(0);
            }, 1000);

        } catch (error) {
            console.error('Split error:', error);
            toast.error(`Failed to split PDF: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    const splitEveryNPages = async () => {
        const n = parseInt(everyNPages);
        if (!n || n < 1 || n > totalPages) {
            toast.warning('Please enter a valid number of pages');
            return;
        }

        try {
            setIsProcessing(true);
            setProgress(0);

            const zip = new JSZip();
            const numFiles = Math.ceil(totalPages / n);
            // Create preview PDF with first part
            const previewPdf = await PDFDocument.create();
            let previewCreated = false;

            for (let i = 0; i < numFiles; i++) {
                const startPage = i * n;
                const endPage = Math.min((i + 1) * n, totalPages);

                setProgress(((i + 1) / numFiles) * 80);

                // Create a new PDF with n pages
                const newPdf = await PDFDocument.create();
                const pageIndices = Array.from({ length: endPage - startPage }, (_, idx) => startPage + idx);
                const copiedPages = await newPdf.copyPages(pdfDoc, pageIndices);
                copiedPages.forEach(page => newPdf.addPage(page));

                const pdfBytes = await newPdf.save();
                zip.file(`part-${i + 1}.pdf`, pdfBytes);

                // Create preview from first part
                if (!previewCreated) {
                    copiedPages.forEach(page => previewPdf.addPage(page));
                    previewCreated = true;
                }
            }

            setProgress(85);

            // Generate preview PDF
            if (previewCreated) {
                const previewBytes = await previewPdf.save();
                const previewBlob = new Blob([previewBytes], { type: 'application/pdf' });
                const timestamp = new Date().toISOString().slice(0, 10);
                const previewFilename = `split-preview-${timestamp}.pdf`;
                
                setPreviewBlob(previewBlob);
                setPreviewFilename(previewFilename);
            }

            setProgress(95);

            // Generate ZIP file
            const zipBlob = await zip.generateAsync({ type: 'blob' });
            setProgress(100);

            const timestamp = new Date().toISOString().slice(0, 10);
            saveAs(zipBlob, `split-pdf-every-${n}-pages-${timestamp}.zip`);

            toast.success(`PDF split into ${numFiles} files! Opening preview...`);

            setTimeout(() => {
                setProgress(0);
            }, 1000);

        } catch (error) {
            console.error('Split error:', error);
            toast.error(`Failed to split PDF: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleSplit = () => {
        if (splitMode === 'pages') {
            splitByPages();
        } else if (splitMode === 'ranges') {
            splitByRanges();
        } else if (splitMode === 'every') {
            splitEveryNPages();
        }
    };

    // Generate live preview based on current selection
    const generateLivePreview = async () => {
        if (!pdfDoc) return;

        try {
            const previewPdf = await PDFDocument.create();

            if (splitMode === 'pages') {
                if (selectedPages.length === 0) {
                    setPreviewBlob(null);
                    return;
                }
                // Add all selected pages to preview
                for (const pageNum of selectedPages) {
                    const [copiedPage] = await previewPdf.copyPages(pdfDoc, [pageNum - 1]);
                    previewPdf.addPage(copiedPage);
                }
            } else if (splitMode === 'ranges') {
                const validRanges = ranges.filter(r => r.start && r.end);
                if (validRanges.length === 0) {
                    setPreviewBlob(null);
                    return;
                }
                // Add first valid range to preview
                const firstRange = validRanges[0];
                const start = parseInt(firstRange.start);
                const end = parseInt(firstRange.end);
                if (start >= 1 && end <= totalPages && start <= end) {
                    const pageIndices = Array.from({ length: end - start + 1 }, (_, idx) => start - 1 + idx);
                    const copiedPages = await previewPdf.copyPages(pdfDoc, pageIndices);
                    copiedPages.forEach(page => previewPdf.addPage(page));
                } else {
                    setPreviewBlob(null);
                    return;
                }
            } else if (splitMode === 'every') {
                const n = parseInt(everyNPages);
                if (!n || n < 1 || n > totalPages) {
                    setPreviewBlob(null);
                    return;
                }
                // Add first part to preview
                const startPage = 0;
                const endPage = Math.min(n, totalPages);
                const pageIndices = Array.from({ length: endPage - startPage }, (_, idx) => startPage + idx);
                const copiedPages = await previewPdf.copyPages(pdfDoc, pageIndices);
                copiedPages.forEach(page => previewPdf.addPage(page));
            }

            const previewBytes = await previewPdf.save();
            const previewBlob = new Blob([previewBytes], { type: 'application/pdf' });
            const timestamp = new Date().toISOString().slice(0, 10);
            const previewFilename = `split-preview-${timestamp}.pdf`;
            
            setPreviewBlob(previewBlob);
            setPreviewFilename(previewFilename);
            // Auto-open preview when it's generated
            if (previewBlob) {
                setShowPreview(true);
            }
        } catch (error) {
            console.error('Preview generation error:', error);
            setPreviewBlob(null);
            setShowPreview(false);
        }
    };

    // Update preview when selection changes
    useEffect(() => {
        if (file && pdfDoc) {
            const timeoutId = setTimeout(() => {
                generateLivePreview();
            }, 300); // Debounce for 300ms
            return () => clearTimeout(timeoutId);
        }
    }, [selectedPages, ranges, everyNPages, splitMode, file, pdfDoc, totalPages]);

    return (
        <div>
            {/* Action Buttons */}
            <ToolActionButtons
                onAdd={() => {
                    document.querySelector('input[type="file"]')?.click();
                }}
                notificationCount={selectedPages.length}
            />

            {/* Header */}
            <div className="tool-header">
                <h1 className="tool-title">
                    <Scissors size={36} style={{ display: 'inline', marginRight: 'var(--spacing-3)', color: 'var(--color-accent)' }} />
                    Split PDF
                </h1>
                <p className="tool-description">
                    Extract specific pages or split your PDF into multiple files. Choose from various splitting options.
                </p>
            </div>

            {/* File Upload */}
            {!file ? (
                <FileUploadZone
                    onFilesSelected={handleFileSelected}
                    acceptedTypes={['application/pdf']}
                    multiple={false}
                    title="Drop PDF file here to split"
                    description="or click to browse"
                />
            ) : (
                <Alert variant="success" title="PDF Loaded">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                            <FileText size={16} />
                            <span>{file.name}</span>
                            <Badge variant="info">{totalPages} pages</Badge>
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                setFile(null);
                                setPdfDoc(null);
                                setTotalPages(0);
                                setSelectedPages([]);
                            }}
                        >
                            Change PDF
                        </Button>
                    </div>
                </Alert>
            )}

            {file && (
                <>
                    {/* Split Mode Selection */}
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Card>
                            <h3 style={{
                                fontSize: 'var(--text-xl)',
                                fontWeight: 'var(--font-bold)',
                                marginBottom: 'var(--spacing-4)'
                            }}>
                                Split Method
                            </h3>

                            <div style={{ display: 'flex', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-4)' }}>
                                <Button
                                    variant={splitMode === 'pages' ? 'primary' : 'ghost'}
                                    onClick={() => setSplitMode('pages')}
                                >
                                    Select Pages
                                </Button>
                                <Button
                                    variant={splitMode === 'ranges' ? 'primary' : 'ghost'}
                                    onClick={() => setSplitMode('ranges')}
                                >
                                    Page Ranges
                                </Button>
                                <Button
                                    variant={splitMode === 'every' ? 'primary' : 'ghost'}
                                    onClick={() => setSplitMode('every')}
                                >
                                    Every N Pages
                                </Button>
                            </div>

                            {/* Select Pages Mode */}
                            {splitMode === 'pages' && (
                                <div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-3)' }}>
                                        <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)' }}>
                                            Click on pages to select them for extraction
                                        </p>
                                        <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                                            <Button variant="ghost" size="sm" onClick={selectAllPages}>
                                                Select All
                                            </Button>
                                            <Button variant="ghost" size="sm" onClick={deselectAllPages}>
                                                Deselect All
                                            </Button>
                                        </div>
                                    </div>

                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
                                        gap: 'var(--spacing-3)',
                                        marginTop: 'var(--spacing-4)'
                                    }}>
                                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                                            <div
                                                key={pageNum}
                                                onClick={() => togglePageSelection(pageNum)}
                                                style={{
                                                    aspectRatio: '1',
                                                    border: selectedPages.includes(pageNum)
                                                        ? '2px solid var(--color-accent)'
                                                        : '1px solid var(--color-border)',
                                                    borderRadius: 'var(--radius-md)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: 'pointer',
                                                    backgroundColor: selectedPages.includes(pageNum)
                                                        ? 'var(--color-accent-subtle)'
                                                        : 'var(--color-surface)',
                                                    transition: 'all var(--transition-base)',
                                                    fontWeight: 'var(--font-semibold)',
                                                    color: selectedPages.includes(pageNum)
                                                        ? 'var(--color-accent)'
                                                        : 'var(--color-text-secondary)'
                                                }}
                                            >
                                                {pageNum}
                                            </div>
                                        ))}
                                    </div>

                                    {selectedPages.length > 0 && (
                                        <Alert variant="info" style={{ marginTop: 'var(--spacing-4)' }}>
                                            {selectedPages.length} page{selectedPages.length !== 1 ? 's' : ''} selected
                                        </Alert>
                                    )}
                                </div>
                            )}

                            {/* Page Ranges Mode */}
                            {splitMode === 'ranges' && (
                                <div>
                                    <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)', marginBottom: 'var(--spacing-3)' }}>
                                        Define page ranges to split the PDF into multiple files
                                    </p>

                                    {ranges.map((range, index) => (
                                        <div key={index} style={{ display: 'flex', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-3)', alignItems: 'center' }}>
                                            <input
                                                type="number"
                                                placeholder="Start"
                                                value={range.start}
                                                onChange={(e) => updateRange(index, 'start', e.target.value)}
                                                className="input"
                                                min="1"
                                                max={totalPages}
                                                style={{ flex: 1 }}
                                            />
                                            <span>to</span>
                                            <input
                                                type="number"
                                                placeholder="End"
                                                value={range.end}
                                                onChange={(e) => updateRange(index, 'end', e.target.value)}
                                                className="input"
                                                min="1"
                                                max={totalPages}
                                                style={{ flex: 1 }}
                                            />
                                            {ranges.length > 1 && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    icon
                                                    onClick={() => removeRange(index)}
                                                >
                                                    <Trash2 size={16} />
                                                </Button>
                                            )}
                                        </div>
                                    ))}

                                    <Button variant="ghost" size="sm" onClick={addRange}>
                                        + Add Range
                                    </Button>
                                </div>
                            )}

                            {/* Every N Pages Mode */}
                            {splitMode === 'every' && (
                                <div>
                                    <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)', marginBottom: 'var(--spacing-3)' }}>
                                        Split the PDF into files with a fixed number of pages each
                                    </p>

                                    <div style={{ display: 'flex', gap: 'var(--spacing-3)', alignItems: 'center' }}>
                                        <label style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)' }}>
                                            Pages per file:
                                        </label>
                                        <input
                                            type="number"
                                            value={everyNPages}
                                            onChange={(e) => setEveryNPages(e.target.value)}
                                            className="input"
                                            min="1"
                                            max={totalPages}
                                            style={{ width: '120px' }}
                                        />
                                    </div>

                                    {everyNPages > 0 && (
                                        <Alert variant="info" style={{ marginTop: 'var(--spacing-4)' }}>
                                            This will create {Math.ceil(totalPages / parseInt(everyNPages))} file{Math.ceil(totalPages / parseInt(everyNPages)) !== 1 ? 's' : ''}
                                        </Alert>
                                    )}
                                </div>
                            )}
                        </Card>
                    </div>

                    {/* Progress Bar */}
                    {isProcessing && (
                        <div style={{ marginTop: 'var(--spacing-6)' }}>
                            <ProgressBar value={progress} striped showLabel />
                        </div>
                    )}

                    {/* Preview and Split Buttons */}
                    <div style={{ marginTop: 'var(--spacing-6)', display: 'flex', gap: 'var(--spacing-3)' }}>
                        {previewBlob && (
                            <Button
                                variant="secondary"
                                size="lg"
                                onClick={() => setShowPreview(true)}
                                style={{ flex: 1 }}
                            >
                                <FileText size={20} style={{ marginRight: 'var(--spacing-2)' }} />
                                View Preview
                            </Button>
                        )}
                        <Button
                            variant="primary"
                            size="lg"
                            onClick={handleSplit}
                            disabled={isProcessing}
                            loading={isProcessing}
                            style={{ flex: previewBlob ? 1 : 'none', width: previewBlob ? 'auto' : '100%' }}
                            fullWidth={!previewBlob}
                        >
                            <Scissors size={20} />
                            Split PDF
                        </Button>
                    </div>
                </>
            )}

            {previewBlob && showPreview && (
                <PDFPreview
                    pdfBlob={previewBlob}
                    filename={previewFilename}
                    onClose={() => {
                        setShowPreview(false);
                        // Don't clear preview blob, just hide the modal
                    }}
                    onDownload={() => {
                        if (previewBlob && previewFilename) {
                            saveAs(previewBlob, previewFilename);
                            toast.success('Download started!');
                        }
                    }}
                />
            )}
        </div>
    );
};

export default SplitPDF;
